import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantLogo, useTenantColors } from '../../contexts/TenantBrandingContext';
import { api } from '../../lib/api';

interface LoginFormData {
  email: string;
  password: string;
}

export const LoginPage: React.FC = () => {
  const { login, isLoading } = useAuth();
  const { logoUrl, logoAlt } = useTenantLogo();
  const { primary } = useTenantColors();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Partial<LoginFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect URL from location state
  const from = (location.state as any)?.from?.pathname || '/';

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginFormData> = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await login(formData);
      
      // Check if redirect to checkout is needed (after email verification with card required)
      // Check both localStorage and sessionStorage for redundancy
      const redirectToCheckout = localStorage.getItem('redirect_to_checkout');
      const redirectAfterLogin = localStorage.getItem('redirect_to_checkout_after_login');
      const redirectAfterAttestation = localStorage.getItem('redirect_to_checkout_after_attestation') || 
                                        sessionStorage.getItem('redirect_to_checkout_after_attestation');
      
      if (redirectToCheckout === 'true' || redirectAfterLogin === 'true' || redirectAfterAttestation === 'true') {
        console.log('LoginPage: Checkout redirect flag found, redirecting to checkout');
        
        localStorage.removeItem('redirect_to_checkout');
        localStorage.removeItem('redirect_to_checkout_after_login');
        localStorage.removeItem('redirect_to_checkout_after_attestation');
        sessionStorage.removeItem('redirect_to_checkout_after_attestation');
        localStorage.removeItem('pending_registration');
        
        // Get plan from stored flag or default to starter
        const plan = localStorage.getItem('checkout_plan_after_login') || 
                     localStorage.getItem('checkout_plan') ||
                     sessionStorage.getItem('checkout_plan') ||
                     'starter';
        console.log('LoginPage: Using plan for checkout:', plan);
        
        localStorage.removeItem('checkout_plan_after_login');
        localStorage.removeItem('checkout_plan');
        sessionStorage.removeItem('checkout_plan');
        
        // Clear any session redirect flags to allow fresh redirect
        sessionStorage.removeItem('incomplete_billing_redirect_attempted');
        sessionStorage.setItem('redirecting_to_checkout', 'true');
        
        // Redirect to checkout with the stored plan
        try {
          console.log('LoginPage: Creating checkout session...');
          const response = await api.billing.createCheckoutSession(
            plan,
            `${window.location.origin}/billing/checkout-success?session_id={CHECKOUT_SESSION_ID}`,
            `${window.location.origin}/login`
          );
          console.log('LoginPage: Got checkout URL, redirecting:', response.data.checkout_url);
          window.location.replace(response.data.checkout_url);
          return;
        } catch (checkoutError: any) {
          console.error('LoginPage: Failed to create checkout session:', checkoutError);
          sessionStorage.removeItem('redirecting_to_checkout');
          // Fall through to normal redirect if checkout fails
        }
      }
      
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle different error types
      if (error.response?.status === 401) {
        setErrors({ email: 'Invalid email or password' });
      } else if (error.response?.status === 422) {
        // Validation errors from backend
        const backendErrors = error.response.data?.detail || {};
        setErrors(backendErrors);
      } else {
        setErrors({ email: 'An error occurred. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name as keyof LoginFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          {/* Logo */}
          <div className="flex justify-center">
            {logoUrl ? (
              <img className="h-12 w-auto" src={logoUrl} alt={logoAlt} />
            ) : (
              <img 
                className="h-12 w-auto" 
                src="/feedbackgate_logo.png" 
                alt="FeedbackGate Logo" 
              />
            )}
          </div>
          
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to="/register"
              className="font-medium"
              style={{ color: primary }}
            >
              create a new account
            </Link>
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={`appearance-none rounded-none relative block w-full px-3 py-2 border ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus-primary focus:z-10 sm:text-sm`}
                placeholder="Email address"
                value={formData.email}
                onChange={handleInputChange}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>
            
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className={`appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus-primary focus:z-10 sm:text-sm`}
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 accent-primary focus-primary border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="font-medium"
                style={{ color: primary }}
              >
                Forgot your password?
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: primary }}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
